import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import MarketingNav from "@/components/MarketingNav";
import api from "@/lib/api";
import { ArrowRight, Globe, AlertCircle } from "lucide-react";

export default function AtlasLanding() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [brand, setBrand] = useState(params.get("brand") || "");
  const [domain, setDomain] = useState(params.get("domain") || "");
  const [countries, setCountries] = useState([]);
  const [language, setLanguage] = useState("en-GB");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "AI Visibility Atlas · Free AEO visibility checker for every brand";
    setMetaDescription(
      "Free AI visibility check across ChatGPT, Claude and Gemini. See whether AI engines recommend your brand in every major locale. One click, no card."
    );
    api.get("/public/atlas/countries").then((r) => setCountries(r.data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!brand.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.post("/public/atlas/scan", {
        brand: brand.trim(),
        domain: domain.trim() || null,
        language,
        utm_source: params.get("utm_source"),
        utm_medium: params.get("utm_medium"),
        utm_campaign: params.get("utm_campaign"),
      });
      navigate(`/atlas/scan/${r.data.atlas_id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not start scan";
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white min-h-screen" data-testid="atlas-landing">
      <MarketingNav />
      <section className="grid-bg">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="pill">
            <Globe size={11} /> AI Visibility Atlas · free report
          </div>
          <h1 className="display mt-6 text-5xl sm:text-6xl lg:text-7xl font-black tracking-[-0.03em] leading-[0.95] max-w-3xl">
            See whether AI engines{" "}
            <span className="inline-block bg-black text-white px-3 pb-1">
              recommend you
            </span>{" "}
            — in every locale.
          </h1>
          <p className="mt-6 text-lg text-zinc-600 max-w-2xl">
            We run 5 hand-picked buyer queries across ChatGPT, Claude, and
            Gemini in your chosen locale. You get a public atlas page you can
            share with your team in under 60 seconds.
          </p>

          <form
            onSubmit={submit}
            className="mt-10 card-flat p-6 grid grid-cols-1 md:grid-cols-12 gap-3 items-end"
          >
            <div className="md:col-span-4">
              <label className="label-eyebrow">Brand name</label>
              <input
                type="text"
                required
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                placeholder="Acme"
                data-testid="atlas-brand"
                className="mt-1 w-full bg-white border border-zinc-200 rounded-[4px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
              />
            </div>
            <div className="md:col-span-4">
              <label className="label-eyebrow">Website (optional)</label>
              <input
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="acme.com"
                data-testid="atlas-domain"
                className="mt-1 w-full bg-white border border-zinc-200 rounded-[4px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
              />
            </div>
            <div className="md:col-span-3">
              <label className="label-eyebrow">Locale</label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                data-testid="atlas-language"
                className="mt-1 w-full bg-white border border-zinc-200 rounded-[4px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
              >
                {countries.map((c) => (
                  <option key={c.language} value={c.language}>
                    {c.country_label} · {c.language}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-1">
              <button
                type="submit"
                disabled={loading || !brand.trim()}
                data-testid="atlas-submit"
                className="w-full inline-flex items-center justify-center gap-1.5 bg-black text-white text-sm px-3 py-2.5 rounded-[4px] hover:bg-zinc-800 transition-colors disabled:opacity-50"
              >
                {loading ? "…" : <ArrowRight size={14} />}
              </button>
            </div>
          </form>

          {error && (
            <div
              className="mt-3 inline-flex items-center gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-[4px] p-2"
              data-testid="atlas-error"
            >
              <AlertCircle size={14} /> {error}
            </div>
          )}

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-zinc-600">
            {[
              ["60 sec", "Runs live, no estimates"],
              ["3 engines", "ChatGPT · Claude · Gemini"],
              ["7 locales", "EN · DE · FR · ES · NL · IT"],
              ["Shareable", "One public URL per atlas"],
            ].map(([k, v]) => (
              <div
                key={k}
                className="border border-zinc-200 rounded-[4px] p-3 bg-white"
              >
                <div className="display text-base font-black">{k}</div>
                <div className="mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 mt-1">
                  {v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function setMetaDescription(text) {
  let tag = document.querySelector('meta[name="description"]');
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", "description");
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", text);
}
